import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Spin, Tag, Image, Input, Modal, Empty, Tooltip, notification, message } from "antd";
import {
  ArrowDownOutlined, ArrowUpOutlined, AudioOutlined, CheckOutlined,
  ClockCircleOutlined, CloseCircleOutlined, CopyOutlined, DeleteOutlined,
  EyeOutlined, FileOutlined, NotificationOutlined, PaperClipOutlined,
  PictureOutlined, PlayCircleOutlined, ReloadOutlined, SendOutlined,
  SmileOutlined, ThunderboltOutlined, TranslationOutlined, WechatOutlined,
} from "@ant-design/icons";
import { API_DOMAIN } from "../constants";
import { AuthContext } from "../contexts/AuthContext";
import { useWatiSocket } from "../contexts/WatiSocketContext";
import {
  CHAT_MESSAGES_API, SEND_TEXT_API, SEND_IMAGE_API, SEND_VOICE_API,
  SEND_VIDEO_API, SEND_DOCUMENT_API, MARK_AS_READ_API,
  WATI_FILE_RULES, WATI_ACCEPT_ATTR, WATI_ACCEPT_LABEL, MB, KB,
} from "./constants";
import {
  _fetchQuickReplies, _fetchTemplates, fmtShortDate, fmtShortDateTime,
  isRtlText, _pastedImageToJpeg, _normalizeBroadcastItem, _normalizeContact,
  expiryDisplay, substituteQRText,
} from "./helpers";
import {
  WaText, MsgStatus, MsgMediaContent, ContactsBubble, BroadcastHeaderMedia,
  QuoteMediaThumb, ChatThemeCtx, MediaScrollCtx, ct,
} from "./MessageParts";
import { GalleryThumb, EmojiPickerPanel, addRecentReaction } from "./ChatPanelGallery";
import { TemplatesModal, TemplatePreviewModal, QuickRepliesModal, QuickReplyPreviewModal } from "./Modals";
import { PIPELINE_COLUMNS } from "./pipeline";

export function ChatPanelMessages({ chat, onImageMessagesChange }) {
  const isDark = React.useContext(ChatThemeCtx);
  const theme = ct(isDark);
  const { authState } = useContext(AuthContext);
  const { watiChatUpdates } = useWatiSocket();
  const [messageText, setMessageText] = useState("");
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [activeTab, setActiveTab] = useState("chat");
  const [galleryTypeFilter, setGalleryTypeFilter] = useState(null);
  const [galleryOwnerFilter, setGalleryOwnerFilter] = useState(null);
  const [customerQueries, setCustomerQueries] = useState([]);
  const [queriesLoading, setQueriesLoading] = useState(false);
  const [scrollToMsgId, setScrollToMsgId] = useState(null);
  const [highlightedMsgId, setHighlightedMsgId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSecs, setRecordingSecs] = useState(0);
  const [pendingAudio, setPendingAudio] = useState(null); // { blob, url }
  const [isSendingAudio, setIsSendingAudio] = useState(false);
  const [quotedMessage, setQuotedMessage] = useState(null); // { id, text, type, owner }
  const [pendingLink, setPendingLink] = useState(null);
  const [inputLaunching, setInputLaunching] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);   // [{ id, file, previewUrl, caption }]
  const [isSendingFiles, setIsSendingFiles] = useState(false);
  const [isSendingText, setIsSendingText] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // tempId → 0-100
  const [isDragOver, setIsDragOver]   = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [stickyDate, setStickyDate]       = useState(null);
  const prevScrollTopRef              = useRef(0);
  const [qrOpen, setQrOpen]           = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrPreview, setQrPreview] = useState(null);
  const [slashTerm, setSlashTerm] = useState(null);   // null = inactive; string = active search term
  const [slashPos, setSlashPos]   = useState(-1);     // index of '/' in messageText
  const [slashHi, setSlashHi]     = useState(0);      // highlighted row index
  const [allQRs, setAllQRs]       = useState(null);   // null = not yet loaded
  const [slashPopupPos, setSlashPopupPos] = useState({ left: 0, bottom: 0, width: 300 });
  const slashAnchorRef = useRef(null);
  const [tmplOpen, setTmplOpen]       = useState(false);
  const [templates, setTemplates]     = useState([]);
  const [tmplLoading, setTmplLoading] = useState(false);
  const [tmplPreview, setTmplPreview] = useState(null);
  const [expiryTick, setExpiryTick] = useState(0); // increments every minute to keep expiry live
  const [localExpiry, setLocalExpiry] = useState(null); // overrides chat.expiry when window reopens via socket
  const [translationCache, setTranslationCache] = useState({}); // msgId → { loading, text }
  const prevExpiryRef = useRef(null);
  const chatRef = useRef(chat);
  const textareaRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const lastCursorRef = useRef(null); // saved cursor pos so blur doesn't lose it
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiPos, setEmojiPos] = useState({ right: 420, bottom: 80 });
  const [reactionPicker, setReactionPicker] = useState(null); // { msgId, pos }
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingSecsRef = useRef(0);
  const cancelOnStopRef = useRef(false);
  const analyserRef = useRef(null);
  const analyserCtxRef = useRef(null);
  const waveAnimRef = useRef(null);
  const waveCanvasRef = useRef(null);
  const waveDataRef = useRef([]);
  const pendingTempMap = useRef(new Map()); // localMessageId → tempId for socket dedup
  const watiMsgProcessedRef = useRef(0);
  const chatIdRef = useRef(null);
  const scrollRef = useRef(null);
  const galleryScrollRef = useRef(null);
  const messagesEnd = useRef(null);
  const restoreScroll = useRef(null);
  const isFirstLoadRef = useRef(false);
  const seekMsgIdRef = useRef(null);
  const seekAttemptsRef = useRef(0);
  const seekDataCompleteRef = useRef(false); // true when backend already sent the full chunk (foundMessageId search)
  const hasMoreRef = useRef(true);

  // Slash-command quick reply picker helpers
  const slashResults = slashTerm !== null && allQRs
    ? allQRs.filter(qr => (qr.name || "").toLowerCase().includes(slashTerm.toLowerCase())).slice(0, 8)
    : [];

  const dismissSlash = () => { setSlashTerm(null); setSlashPos(-1); setSlashHi(0); };

  const selectSlash = (qr) => {
    let text = substituteQRText(qr.text || qr.media?.caption || "", authState);
    // Auto-fill remaining {{param}} placeholders the same way QuickReplyPreviewModal does
    const assignedFirstName = (chat?.agent || "").split(" ")[0];
    const params = [...new Set((text.match(/\{\{([^}]+)\}\}/g) || []).map(m => m.slice(2, -2).trim()))];
    const defaults = {};
    params.forEach(param => {
      const pn = param.toLowerCase().replace(/[\s._]/g, "");
      if (pn === "name") defaults[param] = chat?.customer_name || "";
      else if (pn === "operatorfirstname") defaults[param] = assignedFirstName;
    });
    params.forEach(param => {
      const val = defaults[param];
      if (val) text = text.replace(new RegExp(`\\{\\{${param.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\}\\}`, "g"), val);
    });
    const nativeEl = textareaRef.current?.resizableTextArea?.textArea;
    const cursor = nativeEl?.selectionStart ?? messageText.length;
    const before = messageText.slice(0, slashPos);
    const after  = messageText.slice(cursor);
    const newVal = before + text + after;
    setMessageText(newVal);
    dismissSlash();
    const newCursor = before.length + text.length;
    setTimeout(() => {
      if (nativeEl) { nativeEl.selectionStart = nativeEl.selectionEnd = newCursor; nativeEl.focus(); }
    }, 0);
  };

  const handleMessageChange = (e) => {
    const val = e.target.value;
    setMessageText(val);
    const nativeEl = textareaRef.current?.resizableTextArea?.textArea;
    const cursor = nativeEl?.selectionStart ?? val.length;
    const before = val.slice(0, cursor);
    // match '/' at start or after whitespace followed by non-whitespace chars up to cursor
    const slashMatch = before.match(/(^|[\s\n])\/([\S]*)$/);
    if (slashMatch) {
      const term = slashMatch[2];
      const idx  = before.lastIndexOf("/");
      setSlashPos(idx);
      setSlashTerm(term);
      setSlashHi(0);
      if (!allQRs) _fetchQuickReplies(authState?.id).then(list => setAllQRs(list));
      if (slashAnchorRef.current) {
        const r = slashAnchorRef.current.getBoundingClientRect();
        setSlashPopupPos({ left: r.left, bottom: window.innerHeight - r.top + 4, width: r.width });
      }
    } else {
      dismissSlash();
    }
  };

  const fetchMessages = async (chatId, lastMessageId = null, foundMessageId = null) => {
    setLoadingMsgs(true);
    try {
      const res = await axios.post(
        CHAT_MESSAGES_API,
        { chatId, lastMessageId, userId: authState.id, ...(foundMessageId ? { foundMessageId } : {}) },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (res.data.success) {
        const raw = Array.isArray(res.data.data) ? res.data.data : (res.data.data?.data || []);
        const items = [...raw].reverse().map(item => {
          if (item.eventType === "broadcastMessage") return _normalizeBroadcastItem(item);
          if (item.type === "call_permission_reply") return {
            id: item.id,
            eventType: "ticket",
            eventDescription: item.text || "Call permission reply",
            timestamp: item.timestamp || String(Math.floor(new Date(item.created || Date.now()).getTime() / 1000)),
          };
          return item;
        });
        if (lastMessageId) {
          if (raw.length === 0) {
            notification.info({ message: "No older messages", placement: "topRight", duration: 2 });
            setHasMore(false);
          } else {
            if (scrollRef.current) {
              restoreScroll.current = { scrollTop: scrollRef.current.scrollTop, scrollHeight: scrollRef.current.scrollHeight };
            }
            setMessages(prev => [...items, ...prev]);
            setHasMore(true);
          }
        } else {
          // Re-arm the first-load flag so the scroll-to-bottom always fires when
          // the full message set arrives, even if a socket message already consumed
          // it before this fetch completed.
          isFirstLoadRef.current = true;
          setMessages(items);
          setHasMore(raw.length > 0);
          markChatAsRead();
        }
      }
    } catch {
      message.error("Failed to load messages.");
    } finally {
      setLoadingMsgs(false);
    }
  };

  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  useEffect(() => {
    seekMsgIdRef.current = null;
    seekAttemptsRef.current = 0;
    notification.destroy("seeking-msg");
    setTranslationCache({});
    if (!chat?.conversationId) { setMessages([]); setHasMore(true); setLoadingMsgs(false); return; }
    setMessages([]); setHasMore(true); setLoadingMsgs(false);
    restoreScroll.current = null;
    isFirstLoadRef.current = true;
    prevScrollTopRef.current = 0;
    setShowScrollBtn(false);
    setStickyDate(null);
    if (chat.foundMessageId) {
      seekMsgIdRef.current = chat.foundMessageId;
      seekAttemptsRef.current = 0;
      seekDataCompleteRef.current = true; // backend sends the full chunk — no extra loads needed
      notification.info({ key: "seeking-msg", message: "Loading messages…", description: "Scrolling to the matched message.", placement: "topRight", duration: 0 });
    }
    fetchMessages(chat.conversationId, null, chat.foundMessageId || null);
  }, [chat?.id]); // eslint-disable-line

  useEffect(() => {
    if (restoreScroll.current && scrollRef.current) {
      const { scrollTop, scrollHeight } = restoreScroll.current;
      scrollRef.current.scrollTop = scrollTop + (scrollRef.current.scrollHeight - scrollHeight);
      restoreScroll.current = null;
    }

    if (seekMsgIdRef.current) {
      const targetId = seekMsgIdRef.current;
      const oldestId = messages[0]?.id || null;

      if (seekDataCompleteRef.current) {
        // Start from the bottom so the user sees latest messages first, then scroll up to the match
        messagesEnd.current?.scrollIntoView({ behavior: "auto" });
        // Data is already fully loaded — just poll for the DOM element (large lists need time to paint)
        let polls = 0;
        const poll = () => {
          const el = document.getElementById(`msg-${targetId}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            setHighlightedMsgId(targetId);
            setTimeout(() => setHighlightedMsgId(null), 2000);
            notification.destroy("seeking-msg");
            seekMsgIdRef.current = null;
            seekAttemptsRef.current = 0;
            seekDataCompleteRef.current = false;
          } else if (polls < 20) {
            polls++;
            setTimeout(poll, 150);
          } else {
            notification.destroy("seeking-msg");
            notification.warning({ message: "Message not found", description: "The message could not be found in this chat's history.", placement: "topRight", duration: 3 });
            seekMsgIdRef.current = null;
            seekAttemptsRef.current = 0;
            seekDataCompleteRef.current = false;
          }
        };
        setTimeout(poll, 150);
      } else {
        setTimeout(() => {
          const el = document.getElementById(`msg-${targetId}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            setHighlightedMsgId(targetId);
            setTimeout(() => setHighlightedMsgId(null), 2000);
            notification.destroy("seeking-msg");
            seekMsgIdRef.current = null;
            seekAttemptsRef.current = 0;
          } else if (!hasMoreRef.current) {
            notification.destroy("seeking-msg");
            notification.warning({ message: "Message not found", description: "The message could not be found in this chat's history.", placement: "topRight", duration: 3 });
            seekMsgIdRef.current = null;
            seekAttemptsRef.current = 0;
          } else {
            seekAttemptsRef.current += 1;
            fetchMessages(chat.conversationId, oldestId);
          }
        }, 150);
      }
      return;
    }

    if (messages.length > 0) {
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          messagesEnd.current?.scrollIntoView({ behavior: "auto" });
          updateStickyDate();
        }));
      } else {
        const el = scrollRef.current;
        const isNearBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 300;
        if (isNearBottom) {
          messagesEnd.current?.scrollIntoView({ behavior: "auto" });
        }
      }
    }
  }, [messages]); // eslint-disable-line

  const updateStickyDate = () => {
    const el = scrollRef.current;
    if (!el) return;
    const seps = el.querySelectorAll("[data-date-label]");
    let current = null;
    seps.forEach(sep => {
      if (sep.offsetTop <= el.scrollTop + 48) current = sep.dataset.dateLabel;
    });
    setStickyDate(current);
  };

  const handleScroll = (e) => {
    const el = e.target;
    const curr = el.scrollTop;
    const prev = prevScrollTopRef.current;
    prevScrollTopRef.current = curr;
    const isAtBottom = el.scrollHeight - curr - el.clientHeight < 100;
    const scrollingDown = curr > prev;
    if (isAtBottom) setShowScrollBtn(false);
    else if (scrollingDown) setShowScrollBtn(true);
    else setShowScrollBtn(false);
    if (curr < 60 && hasMore && !loadingMsgs && messages.length > 0) {
      fetchMessages(chat.conversationId, messages[0]?.id || null);
    }
    updateStickyDate();
  };

  // Keep chatIdRef current so the socket effect can compare without stale closure
  useEffect(() => { chatIdRef.current = chat?.id ?? null; setLocalExpiry(null); }, [chat?.id]);
  useEffect(() => { chatRef.current = chat; }, [chat]);

  // When a message is quoted, wait for the quote bar animation (180ms) then
  // scroll so the bubble sits right above the bar with 8px breathing room
  useEffect(() => {
    if (!quotedMessage?.id) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`msg-${quotedMessage.id}`);
      const scrollEl = scrollRef.current;
      if (!el || !scrollEl) return;
      const msgRect = el.getBoundingClientRect();
      const containerRect = scrollEl.getBoundingClientRect();
      const GAP = 8;
      if (msgRect.bottom > containerRect.bottom - GAP) {
        scrollEl.scrollTo({ top: scrollEl.scrollTop + (msgRect.bottom - containerRect.bottom + GAP), behavior: "smooth" });
      } else if (msgRect.top < containerRect.top) {
        scrollEl.scrollTo({ top: scrollEl.scrollTop + (msgRect.top - containerRect.top) - GAP, behavior: "smooth" });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [quotedMessage?.id]); // eslint-disable-line

  // Focus textarea when a message is quoted
  useEffect(() => {
    if (!quotedMessage?.id) return;
    setTimeout(() => textareaRef.current?.focus(), 210); // after quote scroll settles
  }, [quotedMessage?.id]); // eslint-disable-line

  // Focus textarea when the chat drawer opens (skip if window is expired)
  useEffect(() => {
    if (!chat?.id) return;
    const isExpired = chat?.expiry?.isValid() && chat.expiry.isBefore(dayjs());
    if (isExpired) return;
    setTimeout(() => textareaRef.current?.focus(), 120);
  }, [chat?.id]); // eslint-disable-line

  // Tick every 60 s for any chat with a valid expiry so the countdown / "ago" label stays live
  useEffect(() => {
    if (!chat?.expiry?.isValid()) return;
    const id = setInterval(() => setExpiryTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, [chat?.id]); // eslint-disable-line

  // Detect when a previously-expired window reopens (via localExpiry override or prop change)
  const effectiveExpiryVal = (localExpiry ?? chat?.expiry)?.valueOf();
  useEffect(() => {
    const prev = prevExpiryRef.current;
    const curr = localExpiry ?? chat?.expiry;
    if (prev?.isValid() && prev.isBefore(dayjs()) && curr?.isValid() && curr.isAfter(dayjs())) {
      notification.success({
        message: "Conversation window reopened!",
        description: "The customer replied — you can now send regular messages.",
        placement: "topRight",
        duration: 6,
        icon: <span style={{ fontSize: 18 }}>🔓</span>,
      });
    }
    prevExpiryRef.current = curr ? curr.clone() : null;
  }, [effectiveExpiryVal]); // eslint-disable-line

  const markChatAsRead = () => {
    if (localStorage.getItem("wati_read_receipts") === "false") return;
    const chatId = chat?.id;
    if (!chatId) return;
    axios.post(MARK_AS_READ_API, { chatId }, { headers: { accessToken: localStorage.getItem("accessToken") } }).catch(() => { });
  };

  // Socket effect: live message updates for the currently open chat
  useEffect(() => {
    if (watiChatUpdates.length <= watiMsgProcessedRef.current) return;
    const newUpdates = watiChatUpdates.slice(watiMsgProcessedRef.current);
    watiMsgProcessedRef.current = watiChatUpdates.length;

    newUpdates.forEach(update => {

      // ── New message (incoming from customer or outgoing from any operator) ──
      if (update.target === "sendTextMessage") {
        const channelId = update["0"]?.message?.conViewModel?.channelId;
        const mTypeRaw = update["0"]?.messageType || "text";
        // Let template messages through on any channel — they can be sent even from closed/expired chats.
        // All other message types are restricted to the null (default) channel only.
        if (channelId !== null && mTypeRaw !== "template") return;
        const convId = update["0"]?.conversationId;
        if (convId !== chatIdRef.current) return;

        const mType = update["0"].messageType || "text";

        if (mType === "call_permission_reply") {
          const callEvt = {
            id: update["0"].messageId,
            eventType: "ticket",
            eventDescription: update["0"].message.text || "Call permission reply",
            timestamp: String(update["0"].timestamp),
          };
          setMessages(prev => prev.some(m => m.id === callEvt.id) ? prev : [...prev, callEvt]);
          setTimeout(() => {
            const el = scrollRef.current;
            if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
              messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
            }
          }, 50);
          return;
        }

        const isMedia = ["image", "video", "audio", "sticker"].includes(mType);
        const isDoc = mType === "document";
        const isAudio = mType === "audio";
        const isContacts = mType === "contacts";
        const newMsg = {
          id: update["0"].messageId,
          eventType: "message",
          type: mType,
          text: isAudio ? null : update["0"].message.text,
          owner: update["0"].message.isOwner,
          statusString: update["0"].message.statusString,
          timestamp: String(update["0"].timestamp),
          operatorName: update["0"].message.operatorName,
          replySourceMessage: update["0"].replySourceMessage || null,
          messageReferral: update["0"].message.messageReferral || null,
          _animate: true,
          ...((isMedia || isDoc) && { data: isAudio ? (update["0"].message.data || update["0"].message.text || null) : (update["0"].message.data || null) }),
          ...(isContacts && { contacts: (update["0"].message.contacts || []).map(_normalizeContact) }),
        };

        setMessages(prev => {
          const localMsgId = update["0"].localMessageId;
          const tempId = localMsgId ? pendingTempMap.current.get(localMsgId) : undefined;
          if (tempId) {
            pendingTempMap.current.delete(localMsgId);
            const idx = prev.findIndex(m => m.id === tempId);
            if (idx !== -1) {
              const next = [...prev];
              next[idx] = { ...newMsg, _animate: false }; // replacing existing bubble — no re-entrance
              return next;
            }
          }
          if (prev.some(m => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });

        if (update["0"].message.isOwner === false) {
          markChatAsRead();
        }

        setTimeout(() => {
          const el = scrollRef.current;
          if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
            messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
          }
        }, 50);
      }

      // ── Delivery / read-receipt status update on a rendered message ─────────
      if (update.target === "updatingOperatorMessageStatus") {
        const msgData = Array.isArray(update["0"]) ? update["0"][0] : update["0"];
        if (!msgData || msgData.conversationId !== chatIdRef.current) return;

        const localMsgId = msgData.localMessageId;
        const tempId = localMsgId ? pendingTempMap.current.get(localMsgId) : undefined;

        if (tempId) {
          // Replace optimistic bubble with confirmed server message
          pendingTempMap.current.delete(localMsgId);
          const isMediaMsg = ["image", "video", "audio", "document"].includes(msgData.type);
          setMessages(prev => {
            const idx = prev.findIndex(m => m.id === tempId);
            if (idx === -1) return prev;
            const next = [...prev];
            next[idx] = {
              ...next[idx],
              id: msgData.id,
              statusString: msgData.statusString,
              _animate: false,
              _optimistic: false,
              _localPreviewUrl: null,
              ...(isMediaMsg && {
                text: msgData.media?.caption || null,  // caption to display
                data: msgData.text || null,            // file path → signed URL
              }),
            };
            return next;
          });
        } else {
          // Status tick on an already-rendered message — also correct text/data for media types
          const isMediaMsg = ["image", "video", "audio", "document"].includes(msgData.type);
          setMessages(prev => prev.map(m => {
            if (m.id !== msgData.id) return m;
            return {
              ...m,
              statusString: msgData.statusString,
              ...(isMediaMsg && {
                text: msgData.media?.caption || null,
                data: msgData.text || null,
              }),
            };
          }));
        }
      }

      // ── Conversation status / assignment change ──────────────────────────────
      if (update.target === "updatingConversation") {
        const convId = update["0"];
        if (!convId || convId !== chatIdRef.current) return;

        // If ticket just changed to Open (1) and the window was expired, restore messaging
        const newTicket = update["1"];
        if (newTicket?.status === 1) {
          const currentExpiry = chatRef.current?.expiry;
          if (currentExpiry?.isValid() && currentExpiry.isBefore(dayjs())) {
            setLocalExpiry(dayjs().add(24, "hour"));
          }
        }

        const evt = update["3"];
        if (!evt?.eventDescription) return;
        const ticketEvent = {
          id: `ticket_evt_${Date.now()}_${Math.random()}`,
          eventType: "ticket",
          eventDescription: evt.eventDescription,
          timestamp: String(Math.floor(Date.now() / 1000)),
        };
        setMessages(prev => [...prev, ticketEvent]);
        setTimeout(() => {
          const el = scrollRef.current;
          if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
            messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
          }
        }, 50);
        return;
      }

      // ── Broadcast message arriving for this conversation ────────────────────
      if (update.target === "updatingConversationByBroadcast") {
        if (update["0"]?.channelId !== null && update["0"]?.channelId !== undefined) return;
        const msg = update["1"];  // arguments[0] = conversation data, arguments[1] = message data
        if (!msg || msg.conversationId !== chatIdRef.current) return;

        const newMsg = { ..._normalizeBroadcastItem(msg), _animate: true };

        setMessages(prev => {
          const localMsgId = msg.localMessageId;
          const tempId = localMsgId ? pendingTempMap.current.get(localMsgId) : undefined;
          if (tempId) {
            pendingTempMap.current.delete(localMsgId);
            const idx = prev.findIndex(m => m.id === tempId);
            if (idx !== -1) {
              const next = [...prev];
              next[idx] = { ...newMsg, _animate: false };
              return next;
            }
          }
          // Status update (READ, DELIVERED, FAILED, etc.) for an already-rendered broadcast bubble
          const existingIdx = prev.findIndex(m => m.id === newMsg.id);
          if (existingIdx !== -1) {
            const next = [...prev];
            next[existingIdx] = {
              ...next[existingIdx],
              statusString: newMsg.statusString,
              _failedDetail: newMsg._failedDetail,
            };
            return next;
          }
          return [...prev, newMsg];
        });

        setTimeout(() => {
          const el = scrollRef.current;
          if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
            messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
          }
        }, 50);
      }

      // ── Operator message sent from another browser session ───────────────────
      if (update.target === "messageFromOperator") {
        const msg = update["0"];
        if (!msg || msg.conversationId !== chatIdRef.current) return;

        const isMedia = ["image", "video", "audio", "sticker"].includes(msg.type);
        const isDoc = msg.type === "document";
        const isAudio = msg.type === "audio";
        const newMsg = {
          id: msg.id,
          localMessageId: msg.localMessageId,
          eventType: "message",
          type: msg.type || "text",
          text: (isAudio || isDoc) ? (msg.media?.caption || null) : msg.text,
          owner: true,
          statusString: msg.statusString,
          timestamp: String(msg.time || Math.floor(Date.now() / 1000)),
          operatorName: msg.operatorName,
          replySourceMessage: msg.replyContextId ? { id: msg.replyContextId } : null,
          _animate: true,
          ...((isMedia || isDoc) && { data: (isAudio || isDoc) ? (msg.text || null) : (msg.media || null) }),
        };

        setMessages(prev => {
          const localMsgId = msg.localMessageId;
          const tempId = localMsgId ? pendingTempMap.current.get(localMsgId) : undefined;
          if (tempId) {
            pendingTempMap.current.delete(localMsgId);
            const idx = prev.findIndex(m => m.id === tempId);
            if (idx !== -1) {
              const next = [...prev];
              next[idx] = { ...newMsg, _animate: false };
              return next;
            }
          }
          if (prev.some(m => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });

        setTimeout(() => {
          const el = scrollRef.current;
          if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
            messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
          }
        }, 50);
      }
    });
  }, [watiChatUpdates]);

  const ensureChatOpen = async () => {
    if (chat?.ticket_status !== 2) return;
    const ticket = chat?.raw?.ticket;
    if (!ticket) return;
    await axios.post(
      API_DOMAIN + "/wati_messages/change_chat_status",
      {
        conversationId: ticket.conversationId,
        status: "Open",
        lastAssignedAgentId: ticket.lastAssignedAgentId || null,
        ticketId: ticket.id,
        userId: authState.id,
      },
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    );
  };

  const insertEmoji = (emoji) => {
    const nativeEl = textareaRef.current?.resizableTextArea?.textArea;
    if (!nativeEl) { setMessageText(prev => prev + emoji); return; }
    // Use saved cursor (from onBlur) so focus loss doesn't reset it to 0
    const saved = lastCursorRef.current;
    const start = saved?.start ?? nativeEl.selectionStart ?? messageText.length;
    const end   = saved?.end   ?? nativeEl.selectionEnd   ?? messageText.length;
    const next  = messageText.slice(0, start) + emoji + messageText.slice(end);
    const after = start + emoji.length; // UTF-16 code units — NOT [...emoji].length (code points)
    lastCursorRef.current = { start: after, end: after };
    setMessageText(next);
    setTimeout(() => { nativeEl.selectionStart = nativeEl.selectionEnd = after; nativeEl.focus(); }, 0);
  };

  const sendReaction = async (emoji, messageId) => {
    setReactionPicker(null);
    if (emoji) addRecentReaction(emoji);
    const prevMsg = messages.find(m => m.id === messageId);
    const oldReactions = prevMsg?.reactions ?? [];
    const newReactions = emoji
      ? [...oldReactions.filter(r => !r.isOwner), { emoji, isOwner: true }]
      : oldReactions.filter(r => !r.isOwner);
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: newReactions } : m));
    try {
      await axios.post(
        API_DOMAIN + "/wati_messages/send_reaction_to_message",
        { emoji, phone: (chat.phone || "").replace(/^\+/, ""), messageId, userId: authState.id },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
    } catch {
      message.error("Failed to send reaction.");
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: oldReactions } : m));
    }
  };

  const handleSend = async () => {
    if (!messageText.trim() || isSendingText) return;
    setIsSendingText(true);
    setEmojiOpen(false);
    try {
      await ensureChatOpen();
      const text = messageText.trim();
      const localMessageId = uuidv4();
      const tempId = `temp_${Date.now()}`;
      const replyContextId = quotedMessage?.id || null;

      setInputLaunching(true);
      setTimeout(() => setInputLaunching(false), 300);

      const optimisticMsg = {
        id: tempId,
        localMessageId,
        eventType: "message",
        type: "text",
        owner: true,
        text,
        statusString: "SENDING",
        timestamp: String(Math.floor(Date.now() / 1000)),
        _optimistic: true,
        _animate: true,
        replyTo: quotedMessage
          ? { id: quotedMessage.id, text: quotedMessage.text, type: quotedMessage.type, owner: quotedMessage.owner }
          : null,
      };

      pendingTempMap.current.set(localMessageId, tempId);
      setMessages(prev => [...prev, optimisticMsg]);
      setMessageText("");
      setQuotedMessage(null);

      try {
        await axios.post(
          SEND_TEXT_API,
          { whatsappId: chat.phone, text, localMessageId, userId: authState.id, replyContextId, ticket_status: chat?.ticket_status ?? null },
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        setMessages(prev => prev.map(m =>
          m.id === tempId ? { ...m, statusString: "SENT" } : m
        ));
      } catch {
        pendingTempMap.current.delete(localMessageId);
        setMessages(prev => prev.map(m =>
          m.id === tempId ? { ...m, statusString: "FAILED" } : m
        ));
        message.error("Failed to send message.");
      }
    } finally {
      setIsSendingText(false);
    }
  };

  const retrySend = async (failedMsg) => {
    const { id: tempId, localMessageId, text, replyTo } = failedMsg;
    pendingTempMap.current.set(localMessageId, tempId);
    setMessages(prev => prev.map(m =>
      m.id === tempId ? { ...m, statusString: "SENDING" } : m
    ));
    try {
      await axios.post(
        SEND_TEXT_API,
        { whatsappId: chat.phone, text, localMessageId, userId: authState.id, replyContextId: replyTo?.id || null },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, statusString: "SENT" } : m
      ));
    } catch {
      pendingTempMap.current.delete(localMessageId);
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, statusString: "FAILED" } : m
      ));
      message.error("Failed to send message.");
    }
  };

  const isFirefox = navigator.userAgent.includes("Firefox");

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          autoGainControl: true,
          noiseSuppression: false,
          echoCancellation: false,
        },
      });
      audioChunksRef.current = [];
      recordingSecsRef.current = 0;

      // Firefox: route through Web Audio gain node to bypass its broken
      // MediaRecorder audio pipeline which silences normal voice frequencies.
      let recordStream = stream;
      let audioCtx = null;
      if (isFirefox) {
        audioCtx = new AudioContext();
        const source = audioCtx.createMediaStreamSource(stream);
        const gain = audioCtx.createGain();
        gain.gain.value = 3.0;
        const dest = audioCtx.createMediaStreamDestination();
        source.connect(gain);
        gain.connect(dest);
        recordStream = dest.stream;
      }

      const mimeType = (isFirefox
        ? ["audio/ogg;codecs=opus", "audio/webm;codecs=opus"]
        : ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"]
      ).find(t => MediaRecorder.isTypeSupported(t)) || "";

      // ── Waveform analyser (separate AudioContext so it doesn't affect the record pipeline) ──
      try {
        const aCtx = new AudioContext();
        const src = aCtx.createMediaStreamSource(stream);
        const analyser = aCtx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.6;
        src.connect(analyser);
        analyserRef.current = analyser;
        analyserCtxRef.current = aCtx;
        waveDataRef.current = [];

        const buf = new Uint8Array(analyser.frequencyBinCount);
        const WAVE_BARS = 200; // rolling window — more bars = slower scroll
        const SAMPLE_MS = 80;  // one sample every 80 ms → full window ≈ 16 s
        const BAR_GAP = 0.4;
        let lastSampleTime = 0;

        const drawWave = (ts) => {
          if (ts - lastSampleTime >= SAMPLE_MS) {
            lastSampleTime = ts;
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) sum += (buf[i] - 128) ** 2;
            const rms = Math.min(1, Math.sqrt(sum / buf.length) / 12); // sensitive
            waveDataRef.current.push(rms);
            if (waveDataRef.current.length > WAVE_BARS)
              waveDataRef.current.shift(); // rolling — oldest drops off left
          }

          const canvas = waveCanvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext("2d");
            const W = canvas.width, H = canvas.height;
            ctx.clearRect(0, 0, W, H);
            const samples = waveDataRef.current;
            const barW = W / WAVE_BARS; // stays constant regardless of fill level
            for (let i = 0; i < samples.length; i++) {
              const amp = samples[i];
              const h = Math.max(2, amp * (H - 4));
              ctx.fillStyle = `rgba(0,168,132,${(0.3 + amp * 0.7).toFixed(2)})`;
              ctx.fillRect(i * barW + BAR_GAP / 2, (H - h) / 2, Math.max(0.5, barW - BAR_GAP), h);
            }
          }
          waveAnimRef.current = requestAnimationFrame(drawWave);
        };
        requestAnimationFrame(drawWave);
      } catch { /* visualiser is non-critical */ }

      const mr = new MediaRecorder(recordStream, mimeType ? { mimeType } : {});
      mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        audioCtx?.close();
        cancelAnimationFrame(waveAnimRef.current);
        analyserCtxRef.current?.close();
        analyserRef.current = null;
        analyserCtxRef.current = null;
        waveDataRef.current = [];
        const type = mr.mimeType || mimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type });
        setPendingAudio({ blob, url: URL.createObjectURL(blob), duration: recordingSecsRef.current });
        setIsRecording(false);
        setRecordingSecs(0);
        clearInterval(recordingTimerRef.current);
      };
      mr.start(100);
      mediaRecorderRef.current = mr;
      setIsRecording(true);
      setRecordingSecs(0);
      recordingTimerRef.current = setInterval(() => {
        recordingSecsRef.current += 1;
        setRecordingSecs(s => s + 1);
      }, 1000);
    } catch {
      message.error("Microphone access denied.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(recordingTimerRef.current);
  };

  const cancelRecording = () => {
    cancelOnStopRef.current = true;
    stopRecording();
  };

  // When a cancel was requested, discard the blob as soon as onstop sets it
  useEffect(() => {
    if (pendingAudio && cancelOnStopRef.current) {
      cancelOnStopRef.current = false;
      if (pendingAudio.url) URL.revokeObjectURL(pendingAudio.url);
      setPendingAudio(null);
    }
  }, [pendingAudio]); // eslint-disable-line

  const discardAudio = () => {
    if (pendingAudio?.url) URL.revokeObjectURL(pendingAudio.url);
    setPendingAudio(null);
  };

  const addFiles = (fileList) => {
    const accepted = [];
    const rejectedLines = [];

    Array.from(fileList).forEach(file => {
      const rule = WATI_FILE_RULES[file.type];
      if (!rule) {
        rejectedLines.push(`${file.name} — unsupported type`);
      } else if (file.size > rule.maxBytes) {
        const limit = rule.maxBytes >= MB
          ? `${rule.maxBytes / MB} MB`
          : `${rule.maxBytes / KB} KB`;
        rejectedLines.push(`${file.name} — exceeds ${limit} limit for ${rule.category}s`);
      } else {
        accepted.push(file);
      }
    });

    if (rejectedLines.length) {
      notification.warning({
        message: `${rejectedLines.length} file${rejectedLines.length > 1 ? "s" : ""} rejected`,
        description: rejectedLines.join("\n"),
        placement: "topRight",
        duration: 7,
      });
    }

    if (!accepted.length) return;
    const entries = accepted.map(file => ({
      id: uuidv4(),
      file,
      caption: "",
      previewUrl: (file.type.startsWith("image/") || file.type.startsWith("video/"))
        ? URL.createObjectURL(file) : null,
    }));
    setPendingFiles(prev => [...prev, ...entries]);
  };

  const removeFile = (id) => {
    setPendingFiles(prev => {
      const entry = prev.find(f => f.id === id);
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter(f => f.id !== id);
    });
  };

  const updateFileCaption = (id, caption) => {
    setPendingFiles(prev => prev.map(f => f.id === id ? { ...f, caption } : f));
  };

  const getWatiType = (mimeType) => WATI_FILE_RULES[mimeType]?.category || "document";

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const sendFiles = async () => {
    if (!pendingFiles.length || isSendingFiles) return;
    setIsSendingFiles(true);
    await ensureChatOpen();
    const snapshot = [...pendingFiles];
    setPendingFiles([]);

    for (const f of snapshot) {
      const localMessageId = uuidv4();
      const tempId = `temp_${Date.now()}_${Math.random()}`;
      const watiType = getWatiType(f.file.type);

      const optimistic = {
        id: tempId,
        eventType: "message",
        type: watiType,
        text: f.caption || null,
        owner: true,
        statusString: "SENDING",
        timestamp: String(Math.floor(Date.now() / 1000)),
        operatorName: authState?.user?.name || null,
        _optimistic: true,
        _animate: true,
        _localPreviewUrl: f.previewUrl || null,
        data: f.previewUrl ? "_local_" : null, // non-null so MsgMediaContent renders
      };

      pendingTempMap.current.set(localMessageId, tempId);
      setMessages(prev => [...prev, optimistic]);

      try {
        const b64 = await toBase64(f.file);
        const userId = authState.id || authState?.user?.id || authState?.user?._id;
        const head = { headers: { accessToken: localStorage.getItem("accessToken") } };

        const onUploadProgress = e => {
          if (e.total) setUploadProgress(prev => ({ ...prev, [tempId]: Math.round((e.loaded / e.total) * 100) }));
        };

        if (watiType === "image") {
          await axios.post(SEND_IMAGE_API, {
            whatsappId: chat.phone,
            b64Image: b64,
            localMessageId,
            userId,
            mimeType: f.file.type,
            caption: f.caption || "",
            ticket_status: chat?.ticket_status ?? null,
          }, { ...head, onUploadProgress });
        } else if (watiType === "video") {
          await axios.post(SEND_VIDEO_API, {
            whatsappId: chat.phone,
            b64Image: b64,
            localMessageId,
            userId,
            mimeType: f.file.type,
            caption: f.caption || "",
            ticket_status: chat?.ticket_status ?? null,
          }, { ...head, onUploadProgress });
        } else if (watiType === "document") {
          await axios.post(SEND_DOCUMENT_API, {
            whatsappId: chat.phone,
            b64Image: b64,
            localMessageId,
            userId,
            mimeType: f.file.type,
            caption: f.caption || "",
            ticket_status: chat?.ticket_status ?? null,
          }, { ...head, onUploadProgress });
        } else {
          throw new Error(`No API configured for type: ${watiType}`);
        }

        setUploadProgress(prev => { const n = { ...prev }; delete n[tempId]; return n; });
        setMessages(prev => prev.map(m => m.id === tempId ? { ...m, statusString: "SENT" } : m));
      } catch {
        setUploadProgress(prev => { const n = { ...prev }; delete n[tempId]; return n; });
        setMessages(prev => prev.map(m => m.id === tempId ? { ...m, statusString: "FAILED" } : m));
        pendingTempMap.current.delete(localMessageId);
      }

      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    }

    setIsSendingFiles(false);
  };

  const sendVoiceMessage = async () => {
    if (!pendingAudio?.blob || isSendingAudio) return;
    setIsSendingAudio(true);
    await ensureChatOpen();
    try {
      const buffer = await pendingAudio.blob.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = "";
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const b64Audio = btoa(binary);
      await axios.post(
        SEND_VOICE_API,
        { whatsappId: chat.phone, b64Audio, localMessageId: uuidv4(), userId: authState.id, ticket_status: chat?.ticket_status ?? null },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      discardAudio();
    } catch {
      message.error("Failed to send voice message.");
    } finally {
      setIsSendingAudio(false);
    }
  };

  const fmtRecording = s => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  useEffect(() => {
    if (activeTab !== "chat" || !scrollToMsgId) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`msg-${scrollToMsgId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedMsgId(scrollToMsgId);
        setTimeout(() => setHighlightedMsgId(null), 2000);
      }
      setScrollToMsgId(null);
    }, 80);
    return () => clearTimeout(timer);
  }, [activeTab, scrollToMsgId]); // eslint-disable-line

  const viewInChat = (msgId) => {
    setActiveTab("chat");
    setScrollToMsgId(msgId);
  };

  const loadCustomerQueries = async () => {
    if (!chat?.phone) return;
    setQueriesLoading(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/whatsapp/get_querysets",
        { from: "2000-01-01", to: dayjs().format("YYYY-MM-DD"), id: authState.id, search: chat.phone },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (res.data.success) setCustomerQueries(Array.isArray(res.data.data) ? res.data.data : []);
    } catch { setCustomerQueries([]); }
    finally { setQueriesLoading(false); }
  };

  useEffect(() => {
    if (activeTab !== "queries") return;
    loadCustomerQueries();
  }, [activeTab, chat?.phone]); // eslint-disable-line

  const translateMsg = async (msgId, text) => {
    const cached = translationCache[msgId];
    if (cached && !cached.loading) {
      setTranslationCache(prev => { const next = { ...prev }; delete next[msgId]; return next; });
      return;
    }
    const target = (navigator.language || "en").split("-")[0];
    setTranslationCache(prev => ({ ...prev, [msgId]: { loading: true, text: null } }));
    try {
      const res = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${target}&dt=t&q=${encodeURIComponent(text)}`
      );
      const data = await res.json();
      const translated = data?.[0]?.map(chunk => chunk?.[0] || "").join("") || "";
      setTranslationCache(prev => ({ ...prev, [msgId]: { loading: false, text: translated || "(no translation available)" } }));
    } catch {
      setTranslationCache(prev => ({ ...prev, [msgId]: { loading: false, text: "(translation failed)" } }));
    }
  };

  const mediaMessages = useMemo(() =>
    [...messages]
      .filter(m => m.eventType === "message" && ["image", "video", "audio", "document"].includes((m.type || "").toLowerCase()))
      .reverse(),
    [messages]
  );
  const visualMedia = mediaMessages.filter(m => ["image", "video", "document"].includes((m.type || "").toLowerCase()));
  const otherMedia = mediaMessages.filter(m => (m.type || "").toLowerCase() === "audio");

  useEffect(() => {
    onImageMessagesChange?.(visualMedia.filter(m => (m.type || "").toLowerCase() === "image"));
  }, [visualMedia]); // eslint-disable-line

  return (
    <>
      {/* ── Tab bar ── */}
      <div style={{ display: "flex", background: theme.surface, borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
        {[["chat", "Chat"], ["gallery", `Gallery${mediaMessages.length ? ` (${mediaMessages.length})` : ""}`], ["queries", `Queries${customerQueries.length ? ` (${customerQueries.length})` : ""}`]].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            flex: 1, padding: "9px 0", border: "none", background: "transparent", cursor: "pointer",
            fontSize: 13, fontWeight: activeTab === key ? 600 : 400,
            color: activeTab === key ? "#00a884" : theme.textSub,
            borderBottom: `2px solid ${activeTab === key ? "#00a884" : "transparent"}`,
            transition: "color 0.15s, border-color 0.15s",
          }}>{label}</button>
        ))}
      </div>

      {/* ── Chat panel ── */}
      <div
        style={{ flex: 1, position: "relative", overflow: "hidden", background: theme.bg, display: activeTab === "chat" ? "flex" : "none", flexDirection: "column" }}
        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false); }}
        onDrop={e => { e.preventDefault(); setIsDragOver(false); addFiles(e.dataTransfer.files); }}
      >
        {isDragOver && (
          <div style={{ position: "absolute", inset: 0, zIndex: 50, background: isDark ? "rgba(0,168,132,0.2)" : "rgba(0,168,132,0.13)", border: "3px dashed #00a884", borderRadius: 4, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <PaperClipOutlined style={{ fontSize: 48, color: "#00a884" }} />
            <div style={{ fontSize: 17, color: "#00a884", fontWeight: 700, marginTop: 14 }}>Drop files to attach</div>
          </div>
        )}
        <div style={{ position: "absolute", inset: 0, zIndex: 0, backgroundImage: `url(${require("../../assets/bg-chat.png")})`, backgroundSize: "auto", backgroundRepeat: "repeat", backgroundPosition: "top left", filter: theme.bgFilter, opacity: theme.bgOpacity }} />
        <MediaScrollCtx.Provider value={scrollRef}>
          <div ref={scrollRef} onScroll={handleScroll} style={{ position: "relative", zIndex: 1, height: "100%", overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
            {loadingMsgs && messages.length > 0 && <div style={{ textAlign: "center", padding: "4px 0" }}><Spin size="small" /></div>}
            {loadingMsgs && messages.length === 0 && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}><Spin /></div>}
            {!loadingMsgs && messages.length === 0 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <Empty image={<WechatOutlined style={{ fontSize: 48, color: "#4a5568" }} />} imageStyle={{ height: 48 }} description={<span style={{ color: "#6b7280", fontSize: 13 }}>No messages yet</span>} />
              </div>
            )}
            {(() => {
              let prevDateKey = null;
              return messages.flatMap((item) => {
                const ts = item.timestamp ? dayjs(parseInt(item.timestamp) * 1000) : item.created ? dayjs(item.created) : null;
                const elements = [];
                if (ts) {
                  const dateKey = ts.format("YYYY-MM-DD");
                  if (dateKey !== prevDateKey) {
                    prevDateKey = dateKey;
                    const label = ts.isSame(dayjs(), "day") ? "Today" : ts.isSame(dayjs().subtract(1, "day"), "day") ? "Yesterday" : ts.format("DD MMM YYYY");
                    elements.push(
                      <div key={`sep-${dateKey}`} data-date-label={label} style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0" }}>
                        <div style={{ flex: 1, height: 1, background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)" }} />
                        <span style={{ fontSize: 10, color: theme.textSub, background: theme.pill, padding: "2px 10px", borderRadius: 20, fontWeight: 500, boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>{label}</span>
                        <div style={{ flex: 1, height: 1, background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)" }} />
                      </div>
                    );
                  }
                }
                const isToday = ts?.isSame(dayjs(), "day");
                const isYesterday = ts?.isSame(dayjs().subtract(1, "day"), "day");
                const timeStr = ts
                  ? (isToday || isYesterday ? ts.format("hh:mm A") : fmtShortDateTime(ts))
                  : "";
                if (item.eventType === "ticket") {
                  elements.push(
                    <div key={item.id} style={{ display: "flex", justifyContent: "center", margin: "2px 0" }}>
                      <div style={{ background: theme.pill, borderRadius: 20, padding: "3px 12px", fontSize: 11, color: theme.textSub, textAlign: "center", maxWidth: "85%", boxShadow: "0 1px 2px rgba(0,0,0,0.2)" }}>
                        {item.eventDescription}
                        {timeStr && <span style={{ color: "#667781", marginLeft: 6, fontSize: 10 }}>{timeStr}</span>}
                      </div>
                    </div>
                  );
                  return elements;
                }
                if (item.eventType === "message") {
                  const isOut = item.owner === true;
                  const isBroadcast = item.broadcastMessage === true || (item.type || "").toLowerCase() === "broadcastmessage";
                  const msgType = (item.type || "text").toLowerCase();
                  const isMedia = ["image", "video", "audio", "document", "sticker"].includes(msgType);
                  const copyText = item.text || item.caption || "";
                  // Support our optimistic replyTo, legacy replyMsg, and server replySourceMessage
                  const quotedPreview = item.replyTo || item.replyMsg || item.replySourceMessage || null;
                  elements.push(
                    <motion.div key={item.id} id={`msg-${item.id}`}
                      onDoubleClick={() => setQuotedMessage({ id: item.id, text: item.text || item.caption || "", type: item.type || "text", owner: item.owner, data: item.data || null })}
                      style={{ display: "flex", flexDirection: "column", alignItems: isOut ? "flex-end" : "flex-start", cursor: "default", marginBottom: item.reactions?.length > 0 ? 12 : 0 }}
                      initial={item._animate ? { opacity: 0, x: isOut ? 28 : -28, scale: 0.93 } : false}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      transition={{ type: "spring", stiffness: 380, damping: 26, mass: 0.8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, maxWidth: "80%" }}>
                        {isOut && !item._optimistic && (
                          <button onClick={e => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); const PW = 300, PH = 296; const rawLeft = r.left - PW - 4; const left = Math.max(8, Math.min(rawLeft, window.innerWidth - PW - 8)); const bottom = Math.min(window.innerHeight - r.bottom + 4, window.innerHeight - PH - 8); setReactionPicker({ msgId: item.id, pos: { left, bottom } }); }}
                            style={{ background: "rgba(255,255,255,0.82)", border: "none", cursor: "pointer", fontSize: 14, padding: "5px", borderRadius: "50%", lineHeight: 1, color: "#555", flexShrink: 0, boxShadow: "0 1px 4px rgba(0,0,0,0.22)", display: "flex", alignItems: "center", justifyContent: "center" }}><SmileOutlined /></button>
                        )}
                      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", position: "relative", minWidth: 0, flex: "0 1 auto", padding: msgType === "sticker" ? "4px" : isMedia && msgType !== "audio" ? "4px 4px 6px" : "7px 11px", borderRadius: isOut ? "12px 4px 12px 12px" : "4px 12px 12px 12px", backgroundColor: msgType === "sticker" ? "transparent" : isOut ? theme.bubbleOut : theme.bubbleIn, boxShadow: highlightedMsgId === item.id ? "0 0 0 3px #00a884" : msgType === "sticker" ? "none" : "0 1px 2px rgba(0,0,0,0.3)", transition: "box-shadow 0.4s", fontSize: 13, lineHeight: "1.5", color: theme.text }}>
                        {/* Template optimistic header image (already-signed URL) */}
                        {item._headerImageUrl && !item._broadcastRawPath && (
                          <img src={item._headerImageUrl} alt="" style={{ width: 260, height: "auto", maxHeight: 320, display: "block", borderRadius: 6, marginBottom: 4 }} />
                        )}
                        {/* Broadcast header media (lazy-resolved) */}
                        {item._broadcastRawPath && (
                          <BroadcastHeaderMedia rawPath={item._broadcastRawPath} mediaType={item._broadcastMediaType} />
                        )}
                        {/* Quoted reply preview */}
                        {quotedPreview && (
                          <div style={{ borderLeft: "3px solid #00a884", background: "rgba(0,0,0,0.2)", borderRadius: "0 6px 6px 0", padding: "4px 8px", marginBottom: 6, width: "100%", boxSizing: "border-box", cursor: "pointer" }}
                            onClick={() => {
                              const refId = quotedPreview.id;
                              if (!refId) return;
                              const el = document.getElementById(`msg-${refId}`);
                              if (el) {
                                el.scrollIntoView({ behavior: "smooth", block: "center" });
                                setHighlightedMsgId(refId);
                                setTimeout(() => setHighlightedMsgId(null), 2000);
                              } else if (hasMoreRef.current) {
                                seekMsgIdRef.current = refId;
                                seekAttemptsRef.current = 0;
                                notification.info({ key: "seeking-msg", message: "Loading older messages…", description: "Looking for the quoted message.", placement: "topRight", duration: 0 });
                                fetchMessages(chat.conversationId, messages[0]?.id || null);
                              } else {
                                notification.warning({ message: "Message not found", placement: "topRight", duration: 2 });
                              }
                            }}>
                            <div style={{ fontSize: 11, color: "#00a884", fontWeight: 600, marginBottom: 1 }}>
                              {quotedPreview.owner === true ? "You" : (chat?.customer_name || "Them")}
                            </div>
                            <div style={{ fontSize: 11, color: "#8696a0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                              {quotedPreview.text || (
                                quotedPreview.type === "image" ? "Photo" :
                                  quotedPreview.type === "video" ? "Video" :
                                    quotedPreview.type === "audio" ? "Voice message" :
                                      quotedPreview.type === "document" ? "Document" : "(media)"
                              )}
                            </div>
                          </div>
                        )}
                        {item.messageReferral?.url && (() => {
                          const ref = item.messageReferral;
                          let hostname = "";
                          try { hostname = new URL(ref.url).hostname.replace(/^www\./, ""); } catch (_) { hostname = ref.url; }
                          return (
                            <a href={ref.url} target="_blank" rel="noreferrer" style={{ textDecoration: "none", display: "block", alignSelf: "stretch", margin: "0 0 8px" }}>
                              <div style={{
                                borderRadius: 8, overflow: "hidden",
                                border: `1px solid ${isDark ? "#2a3942" : "rgba(0,0,0,0.1)"}`,
                                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                              }}>
                                {ref.media?.url && (
                                  <img src={ref.media.url} alt="" style={{ width: "100%", maxHeight: 140, objectFit: "cover", display: "block" }} />
                                )}
                                <div style={{ padding: "6px 10px 8px" }}>
                                  <div style={{ fontSize: 10, color: "#00a884", marginBottom: 2 }}>{hostname}</div>
                                  {ref.headline && <div style={{ fontSize: 12, fontWeight: 700, color: isDark ? "#e9edef" : "#111b21", lineHeight: 1.3, marginBottom: ref.body ? 2 : 0 }}>{ref.headline}</div>}
                                  {ref.body && <div style={{ fontSize: 11, color: isDark ? "#8696a0" : "#667781", lineHeight: 1.4 }}>{ref.body}</div>}
                                </div>
                              </div>
                            </a>
                          );
                        })()}
                        {isMedia && <MsgMediaContent item={item} />}
                        {msgType === "contacts" && item.contacts?.length > 0 && (
                          <ContactsBubble contacts={item.contacts.map(_normalizeContact)} />
                        )}
                        {!isMedia && msgType !== "contacts" && item.text && (() => {
                          const hasHeaderMedia = !!(item._broadcastRawPath || item._headerImageUrl);
                          const rtl = isRtlText(item.text);
                          return (
                            <div style={{ wordBreak: "break-word", overflowWrap: "break-word", ...(rtl ? { direction: "rtl", textAlign: "right" } : {}), ...(hasHeaderMedia ? { width: 0, minWidth: "100%" } : {}) }}>
                              <WaText text={item.text} onLinkClick={isOut ? null : url => setPendingLink(url)} />
                            </div>
                          );
                        })()}
                        {translationCache[item.id] && (
                          <div style={{ marginTop: 5, paddingTop: 5, borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`, alignSelf: "stretch" }}>
                            {translationCache[item.id].loading
                              ? <Spin size="small" />
                              : <div dir="auto" style={{ fontSize: 12, color: isDark ? "#a8c0b0" : "#4a7a60", fontStyle: "italic", wordBreak: "break-word", overflowWrap: "break-word" }}>{translationCache[item.id].text}</div>
                            }
                          </div>
                        )}
                        {item._footer && (
                          <div style={{ fontSize: 11, color: "#667781", marginTop: 3, alignSelf: "stretch" }}>{item._footer}</div>
                        )}
                        <div style={{ fontSize: 10, color: theme.textSub, marginTop: 4, display: "flex", alignItems: "center", justifyContent: "flex-end", alignSelf: "stretch", gap: 4, paddingRight: isMedia && !["audio", "sticker"].includes(msgType) ? 6 : 0 }}>
                          {isOut && copyText && (
                            <CopyOutlined style={{ fontSize: 11, cursor: "pointer", color: "#667781" }}
                              onClick={() => { navigator.clipboard.writeText(copyText); message.success({ content: "Copied", duration: 1.5 }); }} />
                          )}
                          {isBroadcast && (
                            <Tooltip title="Sent via broadcast">
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 2, color: "#b49ce7", fontWeight: 500, cursor: "default" }}><NotificationOutlined style={{ fontSize: 9 }} /> Broadcast</span>
                            </Tooltip>
                          )}
                          {copyText && (
                            <Tooltip title={translationCache[item.id] ? "Hide translation" : "Translate"}>
                              <TranslationOutlined
                                style={{ fontSize: 11, cursor: "pointer", color: translationCache[item.id] ? "#00a884" : "#667781" }}
                                onClick={() => translateMsg(item.id, copyText)}
                              />
                            </Tooltip>
                          )}
                          {isOut && item.operatorName && <span style={{ fontStyle: "italic" }}>{item.operatorName}</span>}
                          <span>{timeStr}</span>
                          {isOut && (
                            item.statusString === "SENDING" && uploadProgress[item.id] !== undefined
                              ? <span style={{ fontSize: 10, color: theme.textSub, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{uploadProgress[item.id]}%</span>
                              : item._failedDetail && item.statusString === "FAILED"
                                ? <Tooltip title={item._failedDetail}><span><MsgStatus status={item.statusString} /></span></Tooltip>
                                : <MsgStatus status={item.statusString} />
                          )}
                          {isOut && item._optimistic && item.statusString === "FAILED" && (
                            <Tooltip title="Retry sending">
                              <ReloadOutlined
                                style={{ fontSize: 11, color: "#ff4d4f", cursor: "pointer", marginLeft: 1 }}
                                onClick={() => retrySend(item)}
                              />
                            </Tooltip>
                          )}
                          {!isOut && copyText && (
                            <CopyOutlined style={{ fontSize: 11, cursor: "pointer", color: "#667781" }}
                              onClick={() => { navigator.clipboard.writeText(copyText); message.success({ content: "Copied", duration: 1.5 }); }} />
                          )}
                        </div>
                        {item._buttons?.length > 0 && (
                          <div style={{ alignSelf: "stretch", borderTop: "1px solid rgba(0,0,0,0.1)", marginTop: 4 }}>
                            {item._buttons.map((btnText, bi) => (
                              <React.Fragment key={bi}>
                                {bi > 0 && <div style={{ height: 1, background: "rgba(0,0,0,0.1)" }} />}
                                <div style={{ padding: "7px 10px", textAlign: "center", color: "#009de2", fontSize: 12, fontWeight: 500 }}>{btnText}</div>
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                        {/* Reaction badges */}
                        {(item.reactions?.length > 0) && (
                          <div style={{ position: "absolute", bottom: -13, ...(isOut ? { right: 4 } : { left: 4 }), display: "flex", gap: 3, zIndex: 1 }}>
                            {item.reactions.map((rxn, ri) => (
                              <Tooltip key={ri} title={rxn.isOwner ? "You" : (chat?.customer_name || "Customer")}>
                                <div
                                  onClick={rxn.isOwner ? e => { e.stopPropagation(); sendReaction("", item.id); } : undefined}
                                  style={{ background: isDark ? "#2a3942" : "#fff", border: `1px solid ${isDark ? "#3b4a54" : "#e0e0e0"}`, borderRadius: 12, padding: "1px 5px", fontSize: 14, lineHeight: 1.4, cursor: rxn.isOwner ? "pointer" : "default", userSelect: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.15)", display: "inline-flex", alignItems: "center" }}>
                                  {rxn.emoji}
                                </div>
                              </Tooltip>
                            ))}
                          </div>
                        )}
                      </div>
                        {!isOut && !item._optimistic && (
                          <button onClick={e => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); const PW = 300, PH = 296; const rawLeft = r.right + 4; const left = Math.max(8, Math.min(rawLeft, window.innerWidth - PW - 8)); const bottom = Math.min(window.innerHeight - r.bottom + 4, window.innerHeight - PH - 8); setReactionPicker({ msgId: item.id, pos: { left, bottom } }); }}
                            style={{ background: "rgba(255,255,255,0.82)", border: "none", cursor: "pointer", fontSize: 14, padding: "5px", borderRadius: "50%", lineHeight: 1, color: "#555", flexShrink: 0, boxShadow: "0 1px 4px rgba(0,0,0,0.22)", display: "flex", alignItems: "center", justifyContent: "center" }}><SmileOutlined /></button>
                        )}
                      </div>
                    </motion.div>
                  );
                  return elements;
                }
                return elements;
              });
            })()}
            <div ref={messagesEnd} />
          </div>
        </MediaScrollCtx.Provider>
        {stickyDate && (
          <div style={{ position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 20, pointerEvents: "none" }}>
            <span style={{
              fontSize: 11, fontWeight: 500, whiteSpace: "nowrap",
              color: isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.55)",
              background: isDark ? "rgba(32,44,51,0.88)" : "rgba(240,242,245,0.92)",
              padding: "3px 12px", borderRadius: 20,
              boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
              backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
            }}>
              {stickyDate}
            </span>
          </div>
        )}
        {showScrollBtn && (
          <button
            onClick={() => { messagesEnd.current?.scrollIntoView({ behavior: "smooth" }); setShowScrollBtn(false); }}
            style={{
              position: "absolute", bottom: 16, right: 16, zIndex: 20,
              width: 36, height: 36, borderRadius: "50%",
              background: "#00a884", color: "#fff",
              border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 10px rgba(0,0,0,0.25)", fontSize: 16,
              transition: "opacity 0.2s, transform 0.2s",
            }}
          >
            <ArrowDownOutlined />
          </button>
        )}
      </div>

      {/* ── Gallery panel ── */}
      <MediaScrollCtx.Provider value={galleryScrollRef}>
        <div style={{ flex: 1, overflow: "hidden", background: theme.bg, display: activeTab === "gallery" ? "flex" : "none", flexDirection: "column" }}>
          {/* Filter bar */}
          {(() => {
            const chip = (key, icon, label, activeFilter, setFilter) => {
              const active = activeFilter === key;
              return (
                <Tooltip key={key} title={label}>
                  <span onClick={() => setFilter(active ? null : key)}
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", userSelect: "none", border: `1px solid ${active ? "#00a884" : theme.border}`, color: active ? "#00a884" : theme.textSub, background: active ? "rgba(0,168,132,0.12)" : "transparent", transition: "all 0.15s", flexShrink: 0 }}>
                    {icon}
                  </span>
                </Tooltip>
              );
            };
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", borderBottom: `1px solid ${theme.border}`, flexShrink: 0, flexWrap: "wrap" }}>
                {chip("sent", <ArrowUpOutlined style={{ fontSize: 14, color: galleryOwnerFilter === "sent" ? "#00a884" : "#25d366" }} />, "Sent", galleryOwnerFilter, setGalleryOwnerFilter)}
                {chip("received", <ArrowDownOutlined style={{ fontSize: 14, color: galleryOwnerFilter === "received" ? "#00a884" : "#8696a0" }} />, "Received", galleryOwnerFilter, setGalleryOwnerFilter)}
                <span style={{ width: 1, height: 16, background: theme.border, flexShrink: 0 }} />
                {chip("image", <PictureOutlined style={{ fontSize: 14 }} />, "Images", galleryTypeFilter, setGalleryTypeFilter)}
                {chip("video", <PlayCircleOutlined style={{ fontSize: 14 }} />, "Videos", galleryTypeFilter, setGalleryTypeFilter)}
                {chip("document", <FileOutlined style={{ fontSize: 14 }} />, "Docs", galleryTypeFilter, setGalleryTypeFilter)}
                {chip("audio", <AudioOutlined style={{ fontSize: 14 }} />, "Audio", galleryTypeFilter, setGalleryTypeFilter)}
              </div>
            );
          })()}
          {/* Scrollable grid */}
          <div ref={galleryScrollRef} style={{ flex: 1, overflowY: "auto", padding: 2 }}>
            {(() => {
              let items = mediaMessages;
              if (galleryOwnerFilter === "sent") items = items.filter(m => m.owner === true);
              if (galleryOwnerFilter === "received") items = items.filter(m => m.owner !== true);
              if (galleryTypeFilter) items = items.filter(m => (m.type || "").toLowerCase() === galleryTypeFilter);
              if (items.length === 0) return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
                  <Empty image={<PictureOutlined style={{ fontSize: 40, color: "#4a5568" }} />} imageStyle={{ height: 40 }} description={<span style={{ color: "#6b7280", fontSize: 13 }}>{mediaMessages.length === 0 ? "No media yet" : "No matches"}</span>} />
                </div>
              );
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                  {items.map(m => <GalleryThumb key={m.id} item={m} onViewInChat={viewInChat} />)}
                </div>
              );
            })()}
          </div>
        </div>
      </MediaScrollCtx.Provider>

      {/* ── Queries panel ── */}
      {activeTab === "queries" && (
        <div style={{ flex: 1, overflowY: "auto", background: theme.bg, padding: 10 }}>
          {queriesLoading
            ? <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spin /></div>
            : customerQueries.length === 0
              ? <Empty description={<span style={{ color: "#8696a0" }}>No queries found</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 32 }} />
              : (() => {
                // Group by status using PIPELINE_COLUMNS ordering
                const grouped = {};
                customerQueries.forEach(q => {
                  const key = q.status || "Unknown";
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(q);
                });
                // Order groups by PIPELINE_COLUMNS, then any remaining
                const orderedKeys = [];
                PIPELINE_COLUMNS.forEach(col => {
                  col.statuses.forEach(s => { if (grouped[s] && !orderedKeys.includes(s)) orderedKeys.push(s); });
                });
                Object.keys(grouped).forEach(k => { if (!orderedKeys.includes(k)) orderedKeys.push(k); });

                return orderedKeys.map(status => {
                  const col = PIPELINE_COLUMNS.find(c => c.statuses.includes(status));
                  const color = col?.color || "#8696a0";
                  const queries = grouped[status];
                  return (
                    <div key={status} style={{ marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block" }} />
                        <span style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 0.5 }}>{status}</span>
                        <span style={{ fontSize: 10, color: "#8696a0" }}>({queries.length})</span>
                      </div>
                      {queries.map(q => (
                        <div key={q.query_id} style={{ background: theme.surface, borderRadius: 8, padding: "8px 10px", marginBottom: 6, borderLeft: `3px solid ${color}` }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                            <span style={{ fontSize: 10, color: "#8696a0" }}>#{q.query_id}</span>
                            <span style={{ fontSize: 10, color: "#8696a0", flexShrink: 0 }}>
                              {q.date ? dayjs(q.date).format("DD MMM YYYY") : ""}
                            </span>
                          </div>
                          {(q.make || q.model) && (
                            <div style={{ fontSize: 12, fontWeight: 600, color: theme.text, marginBottom: 3 }}>
                              {[q.make, q.model, q.year].filter(Boolean).join(" · ")}
                            </div>
                          )}
                          {q.body_part && (
                            <div style={{ fontSize: 11, color: "#8696a0", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {q.body_part}
                            </div>
                          )}
                          {q.price_given > 0 && (
                            <Tag color="green" style={{ fontSize: 10, margin: 0 }}>AED {q.price_given}</Tag>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                });
              })()
          }
        </div>
      )}

      {activeTab === "chat" && (
        <div style={{ borderTop: `1px solid ${theme.border}`, flexShrink: 0, background: theme.surface }}>
          {/* Quoted message bar */}
          <AnimatePresence initial={false}>
            {quotedMessage && (
              <motion.div
                key="quote-bar"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18, ease: "easeInOut" }}
                style={{ overflow: "hidden" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderBottom: `1px solid ${theme.border}`, background: theme.pill }}>
                  <div style={{ flex: 1, borderLeft: "3px solid #00a884", paddingLeft: 8, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: "#00a884", fontWeight: 600 }}>
                      {quotedMessage.owner === true ? "You" : (chat?.customer_name || "Them")}
                    </div>
                    <div style={{ fontSize: 12, color: "#8696a0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(quotedMessage.type || "").toLowerCase() === "audio"
                        ? <QuoteMediaThumb type="audio" rawPath={quotedMessage.data} />
                        : (quotedMessage.text || (
                            quotedMessage.type === "image" ? "Photo" :
                            quotedMessage.type === "video" ? "Video" :
                            quotedMessage.type === "document" ? "Document" : "(media)"
                          ))}
                    </div>
                  </div>
                  {quotedMessage.data && ["image", "video"].includes((quotedMessage.type || "").toLowerCase()) && (
                    <QuoteMediaThumb type={quotedMessage.type} rawPath={quotedMessage.data} />
                  )}
                  <Button type="text" size="small" icon={<CloseCircleOutlined />}
                    onClick={() => setQuotedMessage(null)} style={{ color: "#8696a0", flexShrink: 0 }} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {/* Pending audio preview */}
          {pendingAudio && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: `1px solid ${theme.border}` }}>
              <audio
                src={pendingAudio.url}
                controls
                style={{ flex: 1, height: 40, minWidth: 0 }}
                onLoadedMetadata={e => {
                  const el = e.currentTarget;
                  if (el.duration === Infinity || isNaN(el.duration)) {
                    el.currentTime = pendingAudio.duration || 9999;
                    el.addEventListener("timeupdate", function fix() {
                      el.removeEventListener("timeupdate", fix);
                      el.currentTime = 0;
                    });
                  }
                }}
              />
              <Tooltip title="Discard">
                <Button size="small" danger icon={<CloseCircleOutlined />} onClick={discardAudio} style={{ flexShrink: 0 }} />
              </Tooltip>
            </div>
          )}
          {/* Pending file attachments */}
          {pendingFiles.length > 0 && (
            <div style={{ display: "flex", gap: 10, padding: "10px 12px", borderBottom: `1px solid ${theme.border}`, flexWrap: "wrap", background: theme.surface }}>
              {pendingFiles.map(f => (
                <div key={f.id} style={{ position: "relative", flexShrink: 0, display: "flex", flexDirection: "column", gap: 5, width: 96 }}>
                  {f.previewUrl && f.file.type.startsWith("image/") ? (
                    <Image src={f.previewUrl} alt="" width={96} height={72} style={{ objectFit: "cover", borderRadius: 7, display: "block" }} preview={{ mask: <EyeOutlined style={{ fontSize: 16 }} /> }} />
                  ) : f.previewUrl && f.file.type.startsWith("video/") ? (
                    <video src={f.previewUrl} style={{ width: 96, height: 72, objectFit: "cover", borderRadius: 7, display: "block" }} muted />
                  ) : (
                    <div style={{ width: 96, height: 72, borderRadius: 7, background: theme.pill, border: `1px solid ${theme.border}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 }}>
                      <FileOutlined style={{ fontSize: 24, color: theme.textSub }} />
                      <span style={{ fontSize: 9, color: theme.textSub, maxWidth: 88, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "0 4px" }}>
                        {f.file.name.split(".").pop().toUpperCase()}
                      </span>
                    </div>
                  )}
                  <Input
                    size="small"
                    placeholder="Caption…"
                    value={f.caption}
                    onChange={e => updateFileCaption(f.id, e.target.value)}
                    style={{ fontSize: 11, borderRadius: 5, background: theme.input, borderColor: theme.border, color: theme.text, width: "100%" }}
                  />
                  <button
                    onClick={() => removeFile(f.id)}
                    style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", border: "none", background: "#ff4d4f", color: "white", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1, zIndex: 1 }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
          {/* Hidden file picker */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={WATI_ACCEPT_ATTR}
            style={{ display: "none" }}
            onChange={e => { if (e.target.files?.length) { addFiles(e.target.files); e.target.value = ""; } }}
          />
          {/* Expired / Solved-state notice */}
          {(() => {
            void expiryTick;
            const effectiveExpiry = localExpiry ?? chat?.expiry;
            const isWindowExpired = effectiveExpiry?.isValid() && effectiveExpiry.isBefore(dayjs());
            if (isWindowExpired) {
              const expiredAt = effectiveExpiry;
              const minsAgo = dayjs().diff(expiredAt, "minute");
              let agoStr;
              if (minsAgo < 60) agoStr = `${minsAgo} minute${minsAgo !== 1 ? "s" : ""} ago`;
              else if (minsAgo < 1440) { const h = Math.floor(minsAgo / 60); const m = minsAgo % 60; agoStr = m > 0 ? `${h}h ${m}m ago` : `${h}h ago`; }
              else { const d = Math.floor(minsAgo / 1440); const h = Math.floor((minsAgo % 1440) / 60); agoStr = h > 0 ? `${d}d ${h}h ago` : `${d}d ago`; }
              const expDateStr = expiredAt.isSame(dayjs(), "day")
                ? `today at ${expiredAt.format("hh:mm A")}`
                : expiredAt.isSame(dayjs().subtract(1, "day"), "day")
                  ? `yesterday at ${expiredAt.format("hh:mm A")}`
                  : expiredAt.format("MMM D [at] hh:mm A");
              return (
                <div style={{ padding: "8px 12px", background: isDark ? "#1a0808" : "#fff1f0", borderTop: `1px solid ${isDark ? "#5c1c1c" : "#ffccc7"}`, display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>🔒</span>
                  <div>
                    <div style={{ fontSize: 11, color: isDark ? "#ff7875" : "#cf1322", fontWeight: 600 }}>Conversation window closed</div>
                    <div style={{ fontSize: 11, color: isDark ? "#ff4d4f" : "#a8071a", marginTop: 2, lineHeight: 1.5 }}>
                      The 24-hour window expired {expDateStr} ({agoStr}). Regular messages are disabled — you can only reach this customer by sending a <strong>Template message</strong>. The conversation will reopen only if the customer replies.
                    </div>
                  </div>
                </div>
              );
            }
            if (chat?.ticket_status === 2) {
              const exp = chat.expiry?.isValid() ? expiryDisplay(chat.expiry) : null;
              const expAbs = exp && chat.expiry?.isValid()
                ? (chat.expiry.isSame(dayjs(), "day") ? `today at ${chat.expiry.format("hh:mm A")}` : `tomorrow at ${chat.expiry.format("hh:mm A")}`)
                : null;
              return (
                <div style={{ padding: "6px 12px", borderBottom: `1px solid ${isDark ? "#5c3d00" : "#ffe58f"}`, background: isDark ? "#2b1d00" : "#fffbe6", display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{ fontSize: 13, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>💬</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 11, color: isDark ? "#ffd666" : "#875400", lineHeight: "1.5" }}>
                      Chat is in <strong>Solved</strong> state — sending a message will reopen it and assign it to you.
                    </span>
                    {exp && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}>
                        <ClockCircleOutlined style={{ fontSize: 10, color: exp.color, flexShrink: 0 }} />
                        <span style={{ fontSize: 10, color: exp.color, fontWeight: 600 }}>{exp.label} remaining</span>
                        {expAbs && <span style={{ fontSize: 10, color: isDark ? "#ad8500" : "#ad6800" }}>· window closes {expAbs}</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            return null;
          })()}
          <AnimatePresence mode="wait" initial={false}>
          {((localExpiry ?? chat?.expiry)?.isValid() && (localExpiry ?? chat?.expiry).isBefore(dayjs())) ? (
            <motion.div key="expired-footer"
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22 }}>
              <div style={{ padding: "8px 12px", display: "flex", justifyContent: "center", borderTop: `1px solid ${isDark ? "#3a2e00" : "#ffe58f"}` }}>
                <Button
                  type="primary"
                  ghost
                  icon={<NotificationOutlined />}
                  style={{ borderRadius: 8 }}
                  onClick={() => {
                    setTmplOpen(true);
                    if (templates.length > 0) return;
                    setTmplLoading(true);
                    _fetchTemplates(authState.id).then(list => { setTemplates(list); setTmplLoading(false); });
                  }}
                >
                  Send Template Message
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div key="active-footer"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}>
          <div style={{ padding: "10px 12px" }}>
            {isRecording ? (
              /* Recording panel — same outer shell as the compound input */
              <div style={{
                display: "flex", flexDirection: "column",
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                background: theme.input,
                overflow: "hidden",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#ff4d4f", flexShrink: 0, animation: "pulse 1s infinite" }} />
                  <span style={{ fontSize: 13, color: theme.text, fontVariantNumeric: "tabular-nums", fontWeight: 600, letterSpacing: 0.5, flexShrink: 0 }}>{fmtRecording(recordingSecs)}</span>
                  <canvas ref={waveCanvasRef} width={240} height={24} style={{ flex: 1, height: 24, display: "block" }} />
                </div>
                <div style={{ display: "flex", alignItems: "center", padding: "2px 4px 4px" }}>
                  <Tooltip title="Cancel recording">
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={cancelRecording} />
                  </Tooltip>
                  <div style={{ flex: 1 }} />
                  <Tooltip title="Stop & review">
                    <Button type="primary" size="small" icon={<CheckOutlined />} onClick={stopRecording} style={{ borderRadius: 6 }}>
                      Done
                    </Button>
                  </Tooltip>
                </div>
              </div>
            ) : (
              /* Compound input box — everything inside */
              <div ref={slashAnchorRef} style={{ position: "relative" }}>
                {slashTerm !== null && slashResults.length > 0 && createPortal(
                  <div style={{
                    position: "fixed",
                    left: slashPopupPos.left,
                    bottom: slashPopupPos.bottom,
                    width: slashPopupPos.width,
                    zIndex: 9999,
                    background: isDark ? "#1f1f1f" : "#fff",
                    border: `1px solid ${isDark ? "#303030" : "#d9d9d9"}`,
                    borderRadius: 8,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                    overflow: "hidden",
                    maxHeight: 280,
                    overflowY: "auto",
                  }}>
                    <div style={{ padding: "4px 10px 2px", fontSize: 10, color: isDark ? "#8c8c8c" : "#bfbfbf", fontWeight: 600, letterSpacing: "0.04em", borderBottom: `1px solid ${isDark ? "#2a2a2a" : "#f0f0f0"}`, marginBottom: 2 }}>
                      QUICK REPLIES — ⚡ {slashTerm ? `"${slashTerm}"` : "type to search"}
                    </div>
                    {slashResults.map((qr, i) => (
                      <div
                        key={qr.id}
                        onMouseDown={e => { e.preventDefault(); selectSlash(qr); }}
                        onMouseEnter={() => setSlashHi(i)}
                        style={{
                          padding: "7px 12px",
                          cursor: "pointer",
                          background: i === slashHi ? (isDark ? "#177ddc22" : "#e6f4ff") : "transparent",
                          borderLeft: i === slashHi ? "3px solid #1677ff" : "3px solid transparent",
                          display: "flex", flexDirection: "column", gap: 1,
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? "#e0e0e0" : "#262626" }}>{qr.name}</span>
                        <span style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#595959", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {substituteQRText(qr.text || qr.media?.caption || "", authState) || <em style={{ color: "#bfbfbf" }}>No text</em>}
                        </span>
                      </div>
                    ))}
                  </div>,
                  document.body
                )}
              <div
                className={[inputLaunching ? "chat-input-launch" : "", isDark ? "qv4-input-dark" : ""].filter(Boolean).join(" ") || undefined}
                style={{
                  display: "flex", flexDirection: "column",
                  border: `1px solid ${chat?.ticket_status === 2 ? (isDark ? "#7c4e00" : "#ffd591") : theme.border}`,
                  borderRadius: 8,
                  background: chat?.ticket_status === 2 ? (isDark ? "#2b1d00" : "#fff7e6") : theme.input,
                  overflow: "hidden",
                }}
              >
                <Input.TextArea
                  ref={textareaRef}
                  value={messageText}
                  onChange={handleMessageChange}
                  placeholder="Type a message…"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  onKeyDown={e => {
                    if (slashTerm !== null && slashResults.length > 0) {
                      if (e.key === "ArrowDown") { e.preventDefault(); setSlashHi(h => Math.min(h + 1, slashResults.length - 1)); return; }
                      if (e.key === "ArrowUp")   { e.preventDefault(); setSlashHi(h => Math.max(h - 1, 0)); return; }
                      if (e.key === "Enter")      { e.preventDefault(); selectSlash(slashResults[slashHi]); return; }
                      if (e.key === "Escape")     { e.preventDefault(); dismissSlash(); return; }
                    }
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (!isSendingText) handleSend(); }
                  }}
                  onSelect={e => { lastCursorRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                  onBlur={e => { lastCursorRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; dismissSlash(); }}
                  onPaste={e => {
                    const files = Array.from(e.clipboardData?.items || [])
                      .filter(i => i.kind === "file" && i.type.startsWith("image/"))
                      .map(i => i.getAsFile())
                      .filter(Boolean);
                    if (files.length) {
                      e.preventDefault();
                      Promise.all(files.map(_pastedImageToJpeg)).then(converted => addFiles(converted));
                    }
                  }}
                  variant="borderless"
                  style={{ background: "transparent", color: theme.text, fontSize: 13, resize: "none", padding: "8px 12px 2px" }}
                />
                <div style={{ display: "flex", alignItems: "center", padding: "2px 4px 4px" }}>
                  <Tooltip title="Emoji" placement="top">
                    <Button ref={emojiBtnRef} type="text" size="small"
                      style={{ color: theme.textSub, fontSize: 16 }}
                      onClick={() => {
                        if (!emojiOpen && emojiBtnRef.current) {
                          const r = emojiBtnRef.current.getBoundingClientRect();
                          setEmojiPos({ right: window.innerWidth - r.left + 8, bottom: window.innerHeight - r.top + 8 });
                        }
                        setEmojiOpen(v => !v);
                      }}>
                      😊
                    </Button>
                  </Tooltip>
                  {emojiOpen && createPortal(
                    <div style={{ position: "fixed", zIndex: 2000, right: emojiPos.right, bottom: emojiPos.bottom, background: "#fff", borderRadius: 10, boxShadow: "0 4px 24px rgba(0,0,0,0.18)", overflow: "hidden" }}>
                      <EmojiPickerPanel onSelect={insertEmoji} onClose={() => setEmojiOpen(false)} />
                    </div>,
                    document.body
                  )}
                  <Tooltip title={WATI_ACCEPT_LABEL} placement="topLeft">
                    <Button type="text" size="small" icon={<PaperClipOutlined />} onClick={() => fileInputRef.current?.click()} style={{ color: theme.textSub, fontSize: 16 }} />
                  </Tooltip>
                  <Tooltip title="Quick replies">
                    <Button type="text" size="small" icon={<ThunderboltOutlined />} style={{ color: "#faad14", fontSize: 16 }}
                      onClick={() => {
                        setQrOpen(true);
                        if (quickReplies.length > 0) return;
                        setQrLoading(true);
                        _fetchQuickReplies(authState.id).then(list => { setQuickReplies(list); setQrLoading(false); });
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Template messages">
                    <Button type="text" size="small" icon={<NotificationOutlined />} style={{ color: "#1677ff", fontSize: 16 }}
                      onClick={() => {
                        setTmplOpen(true);
                        if (templates.length > 0) return;
                        setTmplLoading(true);
                        _fetchTemplates(authState.id).then(list => { setTemplates(list); setTmplLoading(false); });
                      }}
                    />
                  </Tooltip>
                  <div style={{ flex: 1 }} />
                  {messageText.trim() || pendingAudio || pendingFiles.length > 0 ? (
                    <Button type="primary" size="small" icon={<SendOutlined />} loading={isSendingAudio || isSendingFiles || isSendingText} onClick={pendingAudio ? sendVoiceMessage : pendingFiles.length > 0 ? sendFiles : handleSend} style={{ borderRadius: 6 }} />
                  ) : (
                    <Tooltip title={isFirefox ? "Firefox doesn't support voice recording properly, use Chrome/Edge for better results." : "Record voice message"} placement="top">
                      <Button
                        type="default"
                        size="small"
                        icon={<AudioOutlined />}
                        onClick={startRecording}
                        style={{ borderRadius: 6, background: "rgba(0,168,132,0.12)", borderColor: "#00a884", color: "#00a884" }}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
              </div>
            )}
          </div>
          </motion.div>
          )}
          </AnimatePresence>
        </div>
      )}
      {/* Reaction emoji picker — portal, click-outside overlay closes it */}
      {reactionPicker && createPortal(
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 1999 }} onClick={() => setReactionPicker(null)} />
          <div style={{ position: "fixed", zIndex: 2000, left: reactionPicker.pos.left, bottom: reactionPicker.pos.bottom, background: "#fff", borderRadius: 10, boxShadow: "0 4px 24px rgba(0,0,0,0.18)", overflow: "hidden" }}>
            <EmojiPickerPanel
              onSelect={emoji => sendReaction(emoji, reactionPicker.msgId)}
              onClose={() => setReactionPicker(null)}
            />
          </div>
        </>,
        document.body
      )}
      <QuickRepliesModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        quickReplies={quickReplies}
        loading={qrLoading}
        authState={authState}
        onSelect={qr => { setQrPreview(qr); }}
      />
      <QuickReplyPreviewModal
        open={!!qrPreview}
        onClose={() => setQrPreview(null)}
        item={qrPreview}
        authState={authState}
        chat={chat}
        ensureChatOpen={ensureChatOpen}
        onSent={() => { setQrPreview(null); setQrOpen(false); message.success("Quick reply sent!"); }}
      />
      <TemplatesModal
        open={tmplOpen}
        onClose={() => setTmplOpen(false)}
        templates={templates}
        loading={tmplLoading}
        onSelect={t => { setTmplPreview(t); }}
      />
      <TemplatePreviewModal
        open={!!tmplPreview}
        onClose={() => setTmplPreview(null)}
        template={tmplPreview}
        chat={chat}
        authState={authState}
        onSent={({ localMessageId, tempId, text, headerUrl }) => {
          setTmplPreview(null);
          setTmplOpen(false);
          message.success("Template sent!");
          const optimisticMsg = {
            id: tempId,
            localMessageId,
            eventType: "message",
            type: "text",
            text,
            owner: true,
            statusString: "SENDING",
            timestamp: String(Math.floor(Date.now() / 1000)),
            _optimistic: true,
            _animate: true,
            _headerImageUrl: headerUrl || null,
          };
          pendingTempMap.current.set(localMessageId, tempId);
          setMessages(prev => [...prev, optimisticMsg]);
          setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: "smooth" }), 60);
        }}
        onOptimisticFail={({ tempId, localMessageId }) => {
          pendingTempMap.current.delete(localMessageId);
          setMessages(prev => prev.map(m => m.id === tempId ? { ...m, statusString: "FAILED" } : m));
        }}
      />
      <Modal
        open={!!pendingLink}
        title="⚠️ External link"
        onOk={() => { window.open(pendingLink.startsWith("http") ? pendingLink : `https://${pendingLink}`, "_blank", "noopener,noreferrer"); setPendingLink(null); }}
        onCancel={() => setPendingLink(null)}
        okText="Open link"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        width={420}
      >
        <p style={{ marginBottom: 8 }}>This link was received from the customer and <strong>may not be secure</strong>.</p>
        <p style={{ wordBreak: "break-all", background: "#f5f5f5", padding: "6px 10px", borderRadius: 6, fontSize: 12, marginBottom: 8 }}>{pendingLink}</p>
        <p style={{ color: "#888", fontSize: 12, margin: 0 }}>Only open links from sources you trust.</p>
      </Modal>
    </>
  );
}
